import AddIcon from "@mui/icons-material/AddRounded";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { FormikErrorNotification } from "@shared";

import { UserPermission } from "@/common/contexts/current-user-context/CurrentUserContextProvider.types";
import withAuthorization from "@/common/hocs/with-authorization/withAuthorization";
import { Trans, useTranslation } from "@/translations";

import { CategorySelect } from "./components/category-select/CategorySelect";
import { OptionRow } from "./components/option-row/OptionRow";
import { useSystemDropdownsSettings } from "./hooks/useSystemDropdownsSettings";

function SystemDropdownsSettingsInner() {
  const { t } = useTranslation();
  const { formik, categoryDefinition, maxValueLength, isSaving, handleAddRow, handleToggleHidden } =
    useSystemDropdownsSettings();

  return (
    <Stack flex={1} minHeight={0} gap={3} pt={1}>
      <Alert severity="info">
        <Trans i18nKey="settings.systemDropdowns.info" components={{ strong: <strong /> }} />
      </Alert>

      <Box maxWidth={400}>
        <CategorySelect />
      </Box>

      {/* The form is scoped to the value list so Enter inside the category select cannot submit it */}
      <Stack component="form" onSubmit={formik.handleSubmit} flex={1} minHeight={0} gap={3}>
        <FormikErrorNotification formik={formik} />

        <Stack flex={1} minHeight={0} gap={2} sx={{ overflowY: "auto" }}>
          <Typography variant="h6">{t("settings.systemDropdowns.valueList")}</Typography>

          <Stack gap={2}>
            {formik.values.rows.map((row, index) => (
              <OptionRow
                key={`${row.id ?? "new"}-${index}`}
                formik={formik}
                index={index}
                maxValueLength={maxValueLength}
                parameterDefinitions={categoryDefinition?.parameters ?? []}
                onToggleHidden={() => handleToggleHidden(index)}
              />
            ))}
          </Stack>

          <Stack direction="row" justifyContent="flex-end">
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddRow}>
              {t("settings.systemDropdowns.addValue")}
            </Button>
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent="space-between">
          <Button variant="outlined" onClick={formik.handleReset} disabled={isSaving || !formik.dirty}>
            {t("common.resetChanges")}
          </Button>
          <Button type="submit" variant="contained" disabled={isSaving || !formik.dirty}>
            {t("common.save")}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}

export const SystemDropdownsSettings = withAuthorization(SystemDropdownsSettingsInner, UserPermission.Supervisor);
